import { Box } from '@mui/material';
import {
  InputProps,
  regex,
  required,
  FormDataConsumer,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  WrapperField,
  Labeled,
} from 'react-admin';
import { IntegrationRecord } from '../../../common/types';
import IntegrationInfo from './IntegrationInfo';
import StyledArrayInput from './StyledArrayInput';
import StyledBooleanInput from './StyledBooleanInput';

const MewsInputs = ({
  integration,
}: Omit<InputProps, 'source'> & { integration: IntegrationRecord }) => (
  <WrapperField>
    <IntegrationInfo integration={integration} />
    <Box width="70%" maxWidth="70%">
      <TextInput
        source="currentConfig.service.apiBaseUrl"
        label="resources.integrations.fields.mews.apiBaseUrl.label"
        helperText="resources.integrations.fields.mews.apiBaseUrl.helperText"
        type="url"
        validate={required(
          'resources.integrations.fields.mews.apiBaseUrl.helperText'
        )}
        variant="outlined"
        fullWidth
      />
      <TextInput
        source="currentConfig.service.clientToken"
        label="resources.integrations.fields.mews.clientToken.label"
        helperText="resources.integrations.fields.mews.clientToken.helperText"
        validate={required(
          'resources.integrations.fields.mews.clientToken.helperText'
        )}
        variant="outlined"
        fullWidth
      />
      <TextInput
        source="currentConfig.service.accessToken"
        label="resources.integrations.fields.mews.accessToken.label"
        helperText="resources.integrations.fields.mews.accessToken.helperText"
        validate={required(
          'resources.integrations.fields.mews.accessToken.helperText'
        )}
        variant="outlined"
        fullWidth
      />
      <TextInput
        source="currentConfig.service.languageTag"
        label="resources.integrations.fields.mews.languageTag.label"
        helperText="resources.integrations.fields.mews.languageTag.helperText"
        validate={[
          required('resources.integrations.fields.mews.languageTag.helperText'),
          regex(
            /[a-z][a-z]-[A-Z][A-Z]/,
            'resources.integrations.fields.mews.languageTag.helperText'
          ),
        ]}
        variant="outlined"
        fullWidth
      />
      <StyledArrayInput
        source="currentConfig.service.reservationServiceIds"
        label="resources.integrations.fields.mews.reservationServiceIds.label"
        helperText="resources.integrations.fields.mews.reservationServiceIds.helperText"
        variant="outlined"
        validate={required(
          'resources.integrations.fields.mews.reservationServiceIds.helperText'
        )}
        fullWidth
      >
        <SimpleFormIterator disableReordering>
          <TextInput
            source=""
            label="resources.integrations.fields.mews.reservationServiceId.label"
            helperText="resources.integrations.fields.mews.reservationServiceId.helperText"
            variant="outlined"
            fullWidth
          />
        </SimpleFormIterator>
      </StyledArrayInput>
      <StyledBooleanInput
        source="currentConfig.service.useGrossAmount"
        label="resources.integrations.fields.mews.useGrossAmount.label"
        helperText="resources.integrations.fields.mews.useGrossAmount.helperText"
        variant="outlined"
      />
      <TextInput
        source="currentConfig.service.enterpriseId"
        label="resources.integrations.fields.mews.enterpriseId.label"
        helperText="resources.integrations.fields.mews.enterpriseId.helperText"
        variant="outlined"
        fullWidth
      />
      <Labeled
        label="resources.integrations.fields.mews.greenOptionRequest.legend"
        fullWidth
        sx={{
          marginTop: '20px',
          border: '1px solid rgba(0, 0, 0, 0.23)',
          borderRadius: '4px',
          padding: '16px',
          boxSizing: 'border-box',
          '& .RaLabeled-label': {
            position: 'relative',
            top: '-12px',
            left: '8px',
            backgroundColor: 'background.paper',
            padding: '0 6px',
            display: 'inline-block',
          },
        }}
      >
        <Box>
          <SelectInput
            source="currentConfig.features.greenOptionRequest.state"
            choices={[
              { id: 'enabled', name: 'enabled' },
              { id: 'disabled', name: 'disabled' },
            ]}
            label="resources.integrations.fields.mews.greenOptionRequest.state.label"
            helperText="resources.integrations.fields.mews.greenOptionRequest.state.helperText"
            validate={required(
              'resources.integrations.fields.mews.greenOptionRequest.state.helperText'
            )}
            variant="outlined"
            fullWidth
          />
          <FormDataConsumer subscription={{ values: true }}>
            {({ formData }) => {
              const isFeatureEnabled =
                formData?.currentConfig?.features?.greenOptionRequest?.state ===
                'enabled';

              const shouldCreateOrder =
                formData?.currentConfig?.features?.greenOptionRequest
                  ?.sendAsOrder === true;

              const shouldCreateTasks =
                formData?.currentConfig?.features?.greenOptionRequest
                  ?.sendAsTasks === true;

              return (
                <>
                  <SelectInput
                    source="currentConfig.features.greenOptionRequest.confirmationMode"
                    choices={[
                      { id: 'manual', name: 'manual' },
                      { id: 'automatic', name: 'automatic' },
                    ]}
                    label="resources.integrations.fields.mews.greenOptionRequest.confirmationMode.label"
                    helperText="resources.integrations.fields.mews.greenOptionRequest.confirmationMode.helperText"
                    validate={
                      isFeatureEnabled
                        ? required(
                            'resources.integrations.fields.mews.greenOptionRequest.confirmationMode.helperText'
                          )
                        : undefined
                    }
                    disabled={!isFeatureEnabled}
                    variant="outlined"
                    fullWidth
                  />
                  <StyledBooleanInput
                    source="currentConfig.features.greenOptionRequest.sendAsOrder"
                    label="resources.integrations.fields.mews.greenOptionRequest.sendAsOrder.label"
                    helperText="resources.integrations.fields.mews.greenOptionRequest.sendAsOrder.helperText"
                    disabled={!isFeatureEnabled}
                    variant="outlined"
                  />
                  <TextInput
                    source="currentConfig.features.greenOptionRequest.serviceId"
                    label="resources.integrations.fields.mews.greenOptionRequest.serviceId.label"
                    helperText="resources.integrations.fields.mews.greenOptionRequest.serviceId.helperText"
                    validate={
                      isFeatureEnabled && shouldCreateOrder
                        ? required(
                            'resources.integrations.fields.mews.greenOptionRequest.serviceId.helperText'
                          )
                        : undefined
                    }
                    disabled={!isFeatureEnabled || !shouldCreateOrder}
                    variant="outlined"
                    fullWidth
                  />
                  <StyledBooleanInput
                    source="currentConfig.features.greenOptionRequest.sendAsTasks"
                    label="resources.integrations.fields.mews.greenOptionRequest.sendAsTasks.label"
                    helperText="resources.integrations.fields.mews.greenOptionRequest.sendAsTasks.helperText"
                    disabled={!isFeatureEnabled}
                    variant="outlined"
                  />
                  <TextInput
                    source="currentConfig.features.greenOptionRequest.departmentId"
                    label="resources.integrations.fields.mews.greenOptionRequest.departmentId.label"
                    helperText="resources.integrations.fields.mews.greenOptionRequest.departmentId.helperText"
                    disabled={!isFeatureEnabled || !shouldCreateTasks}
                    variant="outlined"
                    fullWidth
                  />
                </>
              );
            }}
          </FormDataConsumer>
        </Box>
      </Labeled>
    </Box>
  </WrapperField>
);

export default MewsInputs;
